import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getSignedStorageUrl } from '@/utils/storageUtils';
import { toast } from 'sonner';

export interface BrandingSettings {
  id: string;
  organization_name: string;
  logo_url: string | null;
  login_banner_url: string | null;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  header_mode: 'light' | 'dark';
}

export const useBranding = () => {
  const queryClient = useQueryClient();

  const { data: branding, isLoading } = useQuery({
    queryKey: ['branding'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('branding_settings')
        .select('*')
        .maybeSingle();

      if (error) throw error;
      
      return data as BrandingSettings || {
        id: '',
        organization_name: 'Shelter Management System',
        logo_url: null,
        login_banner_url: null,
        primary_color: '#2563eb',
        secondary_color: '#eff6ff',
        accent_color: '#dbeafe',
        header_mode: 'light' as const
      };
    }
  });

  const uploadBrandingImage = async (file: File, type: 'logo' | 'banner'): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${type}_${Date.now()}.${fileExt}`;
      const filePath = `branding/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const signedUrl = await getSignedStorageUrl('documents', filePath, 86400); // 24h for branding

      return signedUrl;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
      return null;
    }
  };

  const updateBranding = useMutation({
    mutationFn: async (updates: Partial<BrandingSettings>) => {
      const { data: existing } = await supabase
        .from('branding_settings')
        .select('id')
        .maybeSingle();

      if (existing) {
        const { data, error } = await supabase
          .from('branding_settings')
          .update(updates)
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('branding_settings')
          .insert(updates)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding'] });
      toast.success('Branding updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update branding');
    }
  });

  return {
    branding,
    isLoading,
    updateBranding: updateBranding.mutate,
    uploadBrandingImage,
    isUpdating: updateBranding.isPending
  };
};
